import { List, Map } from "immutable";

import { Assignment } from "seneca-common/features/assignments/state";
import { COURSE_SECTIONS_SPEC_TYPE } from "seneca-common/features/assignments/state/models/spec/CourseSectionsSpec";
import { COURSE_USER_SECTIONS_SPEC_TYPE } from "seneca-common/features/assignments/state/models/spec/CourseUserSectionsSpec";
import {
  getAssignmentSectionIds,
  getSpec,
  getSpecType,
  getUserSectionIds
} from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
import { getUsers } from "seneca-common/features/assignments/state/selectors/state/assignments/assignment/spec/courseUserSectionsSpec";
import {
  composeSelector,
  composeSelectors
} from "seneca-common/utils/selectors/compose-selectors";

import TeacherAssignment from "../../../../models/TeacherAssignment";
import * as assignees from "./assignees";
import * as individualAssigneeSelectors from "./assignees/individualAssignee";
import * as questionAggregations from "./questionAggregations";
import * as sectionAggregations from "./sectionAggregations";

export { isPastAssignment } from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";
export { isAssignmentAfterCutoffDate } from "seneca-common/features/assignments/state/selectors/state/assignments/assignment";

type UserId = string;

export function getAssignees(assignment: TeacherAssignment) {
  return assignment.assignees;
}

export function getAssigneeUserIds(
  assignment: TeacherAssignment
): List<UserId> {
  return getAssignees(assignment).keySeq().toList();
}

export function getNumberOfAssignees(assignment: TeacherAssignment): number {
  return getAssignees(assignment).size;
}

export function getAutoEnrolNewUsers(assignment: TeacherAssignment) {
  return assignment.autoEnrolNewUsers;
}

export const getUsersFromCourseUserSectionsSpec: (
  state: TeacherAssignment
) => Map<UserId, List<string>> = composeSelector(getSpec as any, getUsers);

export function getAssigneeSectionIds(
  assignment: TeacherAssignment,
  userId: string
): List<string> {
  const specType = getSpecType(assignment as Assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      return getAssignmentSectionIds(assignment as Assignment);

    case COURSE_USER_SECTIONS_SPEC_TYPE:
      return getUserSectionIds(assignment as any, userId);
    default:
      return List();
  }
}

export function getAssignmentAssigneesWithSections(
  assignment: TeacherAssignment
): Map<string, List<string>> {
  const specType = getSpecType(assignment as Assignment);

  switch (specType) {
    case COURSE_SECTIONS_SPEC_TYPE:
      const assigneeIds = getAssigneeUserIds(assignment);
      const sectionIds = getAssignmentSectionIds(assignment as Assignment);

      return Map<string, List<string>>().withMutations(mutableUsers =>
        assigneeIds.reduce((acc, assigneeId) => {
          return acc.set(assigneeId, sectionIds);
        }, mutableUsers)
      );
    case COURSE_USER_SECTIONS_SPEC_TYPE:
      return getUsersFromCourseUserSectionsSpec(assignment);
    default:
      return Map<string, List<string>>();
  }
}

function getSectionAggregations(state: TeacherAssignment) {
  return state.sectionAggregations;
}

function getQuestionAggregations(state: TeacherAssignment) {
  return state.questionAggregations;
}

export const getSectionAverageScore = composeSelector(
  getSectionAggregations as any,
  sectionAggregations.getSectionAverageScore
);

export const getQuestionsAverageMark = composeSelector(
  getQuestionAggregations as any,
  questionAggregations.getQuestionsAverageMark
);

export const getQuestionsMarksTotal = composeSelector(
  getQuestionAggregations as any,
  questionAggregations.getQuestionsMarksTotal
);

export const getQuestionAverageMark = composeSelector(
  getQuestionAggregations as any,
  questionAggregations.getAverageMark
);

export const getQuestionMarksTotal = composeSelector(
  getQuestionAggregations as any,
  questionAggregations.getMarksTotal
);

export function isAssignmentWithStats(state: TeacherAssignment) {
  return !!getSectionAggregations(state);
}

export const {
  getAssigneeStatus,
  getAssigneeAggregatedStats,
  getAssigneeSectionStats,
  getAssigneeQuestionStats,
  getAssigneeIndividualSectionStats,
  getAssigneeIndividualQuestionStats,
  getAssignee
} = composeSelectors(getAssignees as any, assignees);

export function getNumberOfAssigneesWithStatus(
  state: TeacherAssignment,
  status: string
): number {
  const assigneeSections = getAssignees(state);

  return assigneeSections!.filter(
    assignee => individualAssigneeSelectors.getStatus(assignee) === status
  ).size;
}

export function getNumberOfAssigneesInProgress(
  state: TeacherAssignment
): number {
  const assigneeSections = getAssignees(state);

  return assigneeSections!.filter(
    assignee =>
      individualAssigneeSelectors.getStatus(assignee) === "incomplete" &&
      individualAssigneeSelectors.getTotalStudyTime(assignee) !== undefined
  ).size;
}
